import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { useToast } from '../../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { 
  Download,
  Copy,
  RefreshCw,
  Calendar,
  Tag,
  FileText,
  X,
  Star,
  StarOff,
  Edit3,
  Trash2
} from 'lucide-react';

const DocumentPreviewModal = ({ document, isOpen, onClose, onDownload, onToggleFavorite, onDelete, onEdit }) => {
  const { toast } = useToast();

  if (!document) return null;

  const categories = [
    { value: 'traffic', label: 'Traffic', color: 'red' },
    { value: 'property', label: 'Property', color: 'green' },
    { value: 'contract', label: 'Contract', color: 'blue' },
    { value: 'criminal-defense', label: 'Criminal Defense', color: 'gray' },
    { value: 'family', label: 'Family', color: 'purple' },
    { value: 'taxation', label: 'Taxation', color: 'yellow' },
    { value: 'estate-trusts', label: 'Estate/Trusts', color: 'indigo' }
  ];

  const getCategoryColor = (category) => {
    const categoryData = categories.find(cat => cat.value === category);
    return categoryData?.color || 'gray';
  };

  const getCategoryBadgeClass = (color) => {
    const colorMap = {
      red: 'bg-red-100 text-red-700 border-red-200',
      green: 'bg-green-100 text-green-700 border-green-200',
      blue: 'bg-blue-100 text-blue-700 border-blue-200',
      gray: 'bg-gray-100 text-gray-700 border-gray-200',
      purple: 'bg-purple-100 text-purple-700 border-purple-200',
      yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200'
    };
    return colorMap[color] || colorMap.gray;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(document.content);
    toast({
      title: "Copied to Clipboard",
      description: "Document content has been copied",
    });
  };

  const downloadAsPDF = () => {
    // In a real implementation, you would generate a proper PDF
    onDownload(document);
    toast({
      title: "Download as PDF",
      description: "PDF generation coming soon. Downloaded as text file.",
    });
  };

  const downloadAsDOC = () => {
    // In a real implementation, you would generate a proper DOC file
    onDownload(document);
    toast({
      title: "Download as DOC",
      description: "DOC generation coming soon. Downloaded as text file.",
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl font-bold text-gray-900 mb-2">
                {document.title}
              </DialogTitle>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Created: {formatDate(document.created_at)}
                </div>
                <Separator orientation="vertical" className="h-4" />
                <div className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  {document.file_size}
                </div>
                <Separator orientation="vertical" className="h-4" />
                <Badge 
                  variant="outline" 
                  className={getCategoryBadgeClass(getCategoryColor(document.category))}
                >
                  <Tag className="w-3 h-3 mr-1" />
                  {categories.find(cat => cat.value === document.category)?.label || document.category}
                </Badge>
                {document.favorite && (
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    <span className="text-yellow-600">Favorited</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        <Separator className="my-4" />

        {/* Document Content */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="bg-white border rounded-lg p-6 font-mono text-sm leading-relaxed whitespace-pre-wrap">
              {document.content}
            </div>
          </ScrollArea>
        </div>

        {/* Action Buttons */}
        <div className="flex-shrink-0 mt-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Button
                onClick={downloadAsPDF}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
              <Button
                onClick={downloadAsDOC}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Download DOC
              </Button>
              <Button
                onClick={copyToClipboard}
                variant="outline"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Text
              </Button>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  toast({
                    title: "Regenerate Feature",
                    description: "Document regeneration coming soon",
                  });
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerate
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  toast({
                    title: "Edit Feature",
                    description: "Document editing coming soon",
                  });
                }}
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentPreviewModal;